import React, { useCallback, useEffect, useState } from "react";
import { DateTime } from "luxon";
import { Bars } from "react-loader-spinner";
import "bootstrap-icons/font/bootstrap-icons.css";
import clockIcon from "../../../assets/images/login/clock.png";
import personIcon from "../../../assets/images/login/parental-control.png";

interface WeatherData {
  name: string;
  main: { temp: number; feels_like: number; humidity: number };
  weather: { description: string; icon: string }[];
  wind?: { speed: number };
  dt: number;
}

interface DashboardInfoCardsProps {
  totalPerson: number;
}

const API_KEY = "d0e227910161de735a8f654402104f43";

const capitalizeWords = (text: string) =>
  text
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const DashboardInfoCards: React.FC<DashboardInfoCardsProps> = ({
  totalPerson,
}) => {
  const [weather, setWeather] = useState<WeatherData | null>(null);
  const [weatherLoading, setWeatherLoading] = useState(true);
  const [weatherError, setWeatherError] = useState<string | null>(null);
  const [meccaTime, setMeccaTime] = useState<DateTime | null>(null);
  const [jakartaTime, setJakartaTime] = useState<DateTime | null>(null);

  const fetchWeather = useCallback(async (lat: number, lon: number) => {
    setWeatherLoading(true);
    setWeatherError(null);
    try {
      const res = await fetch(
        `https://api.openweathermap.org/data/2.5/weather?lat=${lat}&lon=${lon}&appid=${API_KEY}&units=metric&lang=id`,
      );
      if (!res.ok) throw new Error("Gagal mengambil data cuaca.");
      const data: WeatherData = await res.json();
      setWeather(data);
    } catch (e: unknown) {
      const message =
        e instanceof Error ? e.message : "Terjadi kesalahan cuaca.";
      setWeatherError(message);
    } finally {
      setWeatherLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!navigator.geolocation) {
      setWeatherError("Geolocation tidak didukung browser ini.");
      setWeatherLoading(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        fetchWeather(latitude, longitude);
      },
      (geoErr) => {
        setWeatherError(`Gagal mendapatkan lokasi: ${geoErr.message}`);
        setWeatherLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  }, [fetchWeather]);

  useEffect(() => {
    const updateTime = () => {
      setMeccaTime(DateTime.now().setZone("Asia/Riyadh"));
      setJakartaTime(DateTime.now().setZone("Asia/Jakarta"));
    };

    updateTime();
    const intervalId = setInterval(updateTime, 1000);
    return () => clearInterval(intervalId);
  }, []);

  const desc = weather?.weather?.[0]?.description ?? "-";
  const icon = weather?.weather?.[0]?.icon ?? "01d";
  const windKmh = weather?.wind?.speed
    ? Math.round(weather.wind.speed * 3.6)
    : 0;

  return (
    <div className="dashboard-info-row mb-3">
      <div className="dashboard-info-card dashboard-info-card--weather">
        {weatherLoading ? (
          <div className="dashboard-info-card__loader">
            <Bars width="40" height="40" color="#1a2744" />
          </div>
        ) : weatherError ? (
          <div className="dashboard-info-card__error">{weatherError}</div>
        ) : weather ? (
          <div className="weather-card__body">
            <div className="weather-card__location">
              <div className="weather-card__icon-wrap">
                <img
                  className="weather-card__icon"
                  src={`https://openweathermap.org/img/wn/${icon}@2x.png`}
                  alt={desc}
                />
              </div>
              {/* <p className="weather-card__city">{weather.name}</p> */}
              <p className="weather-card__condition">{capitalizeWords(desc)}</p>
            </div>

            <div className="weather-card__temp">
              {Math.round(weather.main.temp)}°C
            </div>

            <div className="weather-card__metrics">
              <div className="weather-card__metric">
                <i className="bi bi-thermometer-half" />
                <span className="weather-card__metric-text">
                  Feels like{" "}
                  <strong>{Math.round(weather.main.feels_like)}°C</strong>
                </span>
              </div>
              <div className="weather-card__metric">
                <i className="bi bi-droplet" />
                <span className="weather-card__metric-text">
                  Humidity <strong>{weather.main.humidity}%</strong>
                </span>
              </div>
              <div className="weather-card__metric">
                <i className="bi bi-wind" />
                <span className="weather-card__metric-text">
                  Wind <strong>{windKmh} km/h</strong>
                </span>
              </div>
            </div>
          </div>
        ) : null}
      </div>

      <div className="dashboard-info-card dashboard-info-card--time">
        <div className="time-card__header">
          <img
            src={clockIcon}
            alt=""
            className="time-card__header-icon"
            aria-hidden="true"
          />
          <span>Makkah Time</span>
        </div>
        <p className="time-card__clock">
          {meccaTime ? meccaTime.toFormat("hh:mm a") : "--:-- --"}
        </p>
        <p className="time-card__date">
          {meccaTime ? meccaTime.toFormat("MMM dd, yyyy") : "-"}
        </p>
      </div>

      <div className="dashboard-info-card dashboard-info-card--time">
        <div className="time-card__header">
          <img
            src={clockIcon}
            alt=""
            className="time-card__header-icon"
            aria-hidden="true"
          />
          <span>Indonesia Time (WIB)</span>
        </div>
        <p className="time-card__clock">
          {jakartaTime ? jakartaTime.toFormat("hh:mm a") : "--:-- --"}
        </p>
        <p className="time-card__date">
          {jakartaTime ? jakartaTime.toFormat("MMM dd, yyyy") : "-"}
        </p>
      </div>

      <div className="dashboard-info-card dashboard-info-card--time">
        <div className="time-card__header">
          <img
            src={personIcon}
            alt=""
            className="time-card__header-icon"
            aria-hidden="true"
          />
          <span>Total Person</span>
        </div>
        <p className="time-card__clock">{totalPerson}</p>
        <p className="time-card__date">Members</p>
      </div>
    </div>
  );
};

export default DashboardInfoCards;
